import {
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from '@bpinternal/ui-kit-next'
import { Badge, JSONTree } from '~/elements'
import { EllipsisVertical, FileJson, ArrowLeft, Check } from 'lucide-react'
import { sentimentStyles, sentimentLabels } from './index'
import { Conversation } from '@botpress/client'
import { useState } from 'react'
import { useMediaQuery } from 'usehooks-ts'
import { useNavigate } from '@tanstack/react-router'
import { cn, copyToClipboard } from '~/utils'

type ConversationHeaderProps = {
  workspaceId: string
  botId: string
  conversation: Conversation
  className?: string
}

export const ConversationHeader = ({ workspaceId, botId, conversation, className }: ConversationHeaderProps) => {
  const [openDropdownMenu, setOpenDropownMenu] = useState<boolean>(false) // Need to control dropdown because of a bug in radix
  const [openDialog, setOpenDialog] = useState<boolean>(false)
  const isMobile = useMediaQuery('(max-width: 768px)')
  const navigate = useNavigate()

  return (
    <div className={cn(className, 'flex flex-col gap-4')}>
      <div className="flex justify-between">
        {isMobile ? (
          <button
            onClick={() => {
              navigate({
                to: '/workspaces/$workspaceId/bots/$botId/conversations',
                params: {
                  workspaceId,
                  botId,
                },
              })
            }}
          >
            <ArrowLeft size={25} />
          </button>
        ) : (
          <h1 className="text-xl font-semibold">
            {conversation.tags.title ??
              (conversation.tags.isDirty === 'true' ? 'Conversation Topic Pending' : 'Conversation Topic Unknown')}
          </h1>
        )}
        <DropdownMenu open={openDropdownMenu} onOpenChange={setOpenDropownMenu}>
          <DropdownMenuTrigger asChild>
            <button className="rounded-md hover:bg-gray-3">
              <EllipsisVertical size={25} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={() => {
                setOpenDropownMenu(false)
                setOpenDialog(true)
              }}
            >
              <div className="flex items-center gap-2">
                <p className="text-sm">Show as JSON</p>
                <FileJson size={16} />
              </div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <h1 className="text-xl font-semibold md:hidden">{conversation.tags.title ?? 'Conversation Topic Unknown'}</h1>
      <div className="flex items-center gap-2">
        {(conversation.tags.sentiment || conversation.tags.isDirty === 'true') && (
          <Badge variant="secondary" className={sentimentStyles[conversation.tags.sentiment ?? 'pending']}>
            {sentimentLabels[conversation.tags.sentiment ?? 'pending']}
          </Badge>
        )}
        {conversation.tags.message_count && (
          <p className="text-sm text-muted-foreground">
            {`${conversation.tags.message_count} ${conversation.tags.message_count === '1' ? 'Message' : 'Messages'}`}
          </p>
        )}
      </div>
      <ConversationDialog conversation={conversation} isOpen={openDialog} setIsOpen={setOpenDialog} />
    </div>
  )
}

type ConversationDialogProps = {
  isOpen: boolean
  setIsOpen: (open: boolean) => void
  conversation: Conversation
}

const ConversationDialog = ({ isOpen, setIsOpen, conversation }: ConversationDialogProps) => {
  const [displayCheck, setDisplayCheck] = useState<boolean>(false)
  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent
        className="rounded-xl"
        onAnimationEnd={() => {
          if (!isOpen) setDisplayCheck(false)
        }}
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">Conversation JSON</DialogTitle>
        </DialogHeader>
        <JSONTree data={conversation} hideRoot />
        <DialogFooter className="flex flex-col gap-2">
          <Button
            variant="secondary"
            className="flex gap-1"
            onClick={(e) => {
              setDisplayCheck(true)
              copyToClipboard(JSON.stringify(conversation), 'Message JSON', {
                preventDefault: false,
                event: e,
                showToast: false,
              })
            }}
          >
            {displayCheck ? (
              <>
                Copied
                <Check size={16} />
              </>
            ) : (
              'Copy to clipboard'
            )}
          </Button>
          <DialogClose asChild>
            <Button variant="default" className="bg-gray-12 text-gray-1 hover:bg-gray-11">
              Close
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
